import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, LogOut, ArrowLeft, ChevronDown, ChevronUp, Search, Download } from "lucide-react";
import { ADMIN_TOKEN_KEY, adminAuthHeader, getAdminToken } from "@/hooks/useAdminAuth";
import { EVENT_GROUP_COLOR, eventDescriptor, eventLabel, formatEvent, isHighPriorityEvent } from "@/utils/eventLabels";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return iso || "—"; }
};

export default function AdminEventsPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [hotOnly, setHotOnly] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!getAdminToken()) { navigate("/admin/dashboard"); return; }
    axios.get(`${API}/admin/funnel-events?limit=500`, { headers: adminAuthHeader() })
      .then(({ data }) => setEvents(data.events || []))
      .catch(() => { sessionStorage.removeItem(ADMIN_TOKEN_KEY); navigate("/admin/dashboard"); })
      .finally(() => setLoading(false));
  }, [navigate]);

  const eventTypes = useMemo(() => Array.from(new Set(events.map(e => e.event_name))).sort(), [events]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter(ev => {
      if (hotOnly && !isHighPriorityEvent(ev.event_name)) return false;
      if (filterType && ev.event_name !== filterType) return false;
      if (!q) return true;
      const blob = JSON.stringify(ev).toLowerCase();
      return blob.includes(q);
    });
  }, [events, search, filterType, hotOnly]);

  const exportCsv = () => {
    const rows = [["timestamp", "event", "user_email", "user_name", "payload"]];
    filtered.forEach(ev => {
      const p = ev.payload || {};
      rows.push([
        ev.created_at || "",
        ev.event_name || "",
        p.email || p.user?.email || "",
        p.name || p.user?.name || "",
        JSON.stringify(p).replace(/"/g, '""'),
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${String(c)}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `eventi_${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const handleLogout = () => { sessionStorage.removeItem(ADMIN_TOKEN_KEY); navigate("/"); };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--np-tertiary)" }}><Loader2 className="animate-spin" size={26} style={{ color: "var(--np-secondary)" }} /></div>;

  return (
    <main className="min-h-screen pb-20" style={{ background: "var(--np-tertiary)" }} data-testid="admin-events-page">
      <header className="border-b" style={{ background: "var(--np-quaternary)", borderColor: "rgba(26,42,58,0.06)" }}>
        <div className="mx-auto px-6 md:px-10 py-3 flex items-center justify-between" style={{ maxWidth: 1400 }}>
          <div className="flex items-center gap-6">
            <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--np-primary)" }}>
              <ArrowLeft size={15} /> Dashboard
            </Link>
            <h1 className="text-lg md:text-xl font-bold" style={{ fontFamily: "Merriweather, serif", color: "var(--np-primary)" }}>Storico Eventi</h1>
          </div>
          <button onClick={handleLogout} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border" style={{ color: "var(--np-primary)", borderColor: "#dbe3ec" }}>
            <LogOut size={13} /> Esci
          </button>
        </div>
      </header>

      <section className="mx-auto px-6 md:px-10 pt-8" style={{ maxWidth: 1400 }}>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--np-muted)" }} />
            <input data-testid="events-search" type="text" placeholder="Cerca email, nome, ID…" value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 h-10 rounded-lg border bg-white text-sm" style={{ borderColor: "#dbe3ec" }} />
          </div>
          <select data-testid="events-filter-type" value={filterType} onChange={e => setFilterType(e.target.value)}
            className="h-10 rounded-lg border bg-white px-3 text-sm" style={{ borderColor: "#dbe3ec", color: "var(--np-primary)" }}>
            <option value="">Tutti gli eventi ({events.length})</option>
            {eventTypes.map(t => <option key={t} value={t}>{eventLabel(t)}</option>)}
          </select>
          <button data-testid="events-hot-toggle" onClick={() => setHotOnly(v => !v)}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg text-sm font-semibold transition"
            style={hotOnly
              ? { background: "var(--np-secondary)", color: "#fff", border: "1px solid var(--np-secondary)" }
              : { background: "#fff", color: "var(--np-primary)", border: "1px solid #dbe3ec" }}>
            🔥 Solo HOT {hotOnly && `(${filtered.length})`}
          </button>
          <button data-testid="events-export-csv" onClick={exportCsv}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-white border text-sm font-semibold" style={{ borderColor: "#dbe3ec", color: "var(--np-primary)" }}>
            <Download size={14} /> Export CSV ({filtered.length})
          </button>
        </div>

        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: "rgba(26,42,58,0.08)" }}>
          <table className="w-full text-sm" data-testid="events-table">
            <thead style={{ background: "var(--np-quaternary)" }}>
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold w-[150px]" style={{ color: "var(--np-primary)" }}>Quando</th>
                <th className="px-4 py-3 font-semibold w-[230px]" style={{ color: "var(--np-primary)" }}>Evento</th>
                <th className="px-4 py-3 font-semibold" style={{ color: "var(--np-primary)" }}>Utente / Dettagli</th>
                <th className="px-4 py-3 font-semibold w-[40px]"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ev, i) => {
                const idx = ev._idx || `${ev.created_at}-${i}`;
                const isOpen = !!expanded[idx];
                const p = ev.payload || {};
                const email = p.email || p.user?.email || "—";
                const name = p.name || p.user?.name || "";
                const c = EVENT_GROUP_COLOR[eventDescriptor(ev.event_name).group] || "#94a3b8";
                const d = formatEvent(ev.event_name, ev.payload || {});
                const isHot = isHighPriorityEvent(ev.event_name);
                return (
                  <React.Fragment key={idx}>
                  <tr className="border-t cursor-pointer hover:bg-gray-50" style={{ borderColor: "rgba(26,42,58,0.06)", background: isHot ? "#fffaf6" : undefined }} onClick={() => {
                    const uid = p.user_id || p.user?.id;
                    if (uid) window.location.href = `/admin/utente/${uid}`;
                    else setExpanded({ ...expanded, [idx]: !isOpen });
                  }}>
                      <td className="px-4 py-3 text-[12px]" style={{ color: "var(--np-muted)" }}>
                        {isHot && <span className="inline-block mr-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white align-middle" style={{ background: "var(--np-secondary)" }}>HOT</span>}
                        {formatDate(ev.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold" style={{ color: "var(--np-primary)" }}>
                            <span className="mr-1">{d.icon}</span>{d.label}
                          </span>
                          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: c }}></span>
                        </div>
                        {d.label !== ev.event_name && (
                          <p className="text-[10px] font-mono mt-0.5" style={{ color: "var(--np-muted)" }}>{d.sublabel || ev.event_name}</p>
                        )}
                      </td>
                      <td className="px-4 py-3" style={{ color: "var(--np-primary)" }}>
                        <span className="font-semibold">{name || "—"}</span>
                        <span className="text-[12px] ml-2" style={{ color: "var(--np-muted)" }}>{email}</span>
                      </td>
                      <td className="px-4 py-3 text-right">{isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t" style={{ borderColor: "rgba(26,42,58,0.04)", background: "#FAFBFD" }}>
                        <td colSpan={4} className="px-4 py-3">
                          <pre className="text-[11px] whitespace-pre-wrap break-all" style={{ color: "var(--np-muted)", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
                            {JSON.stringify(p, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-sm" style={{ color: "var(--np-muted)" }}>Nessun evento corrisponde ai filtri.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
